/* =========================================================================
   ClueVine — Game context (the orchestrator)
   Owns the active puzzle session and drives the play loop uniformly for every
   mode: start → validate/reduce moves → hints → finalize (score + record).

   Daily play is fixed (same puzzle + difficulty for everyone) and counts once:
   a daily already finished today can't be restarted, only viewed. Endless play
   honors the chosen difficulty and the adaptive easing.

   In-progress puzzles are parked, not discarded: every daily and each mode's
   endless puzzle keeps its own saved slot, so leaving a board (or starting a
   different one) never loses progress, and a refresh resumes where you were.
   ========================================================================= */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { adaptDifficulty, resolveDifficulty } from '../engine/difficulty';
import { getMode, requireMode } from '../engine/registry';
import { dailySeed, dateForDay, dayNumber, endlessSeed, type SeedInfo } from '../engine/seed';
import { createPuzzle, finalizeScore } from '../engine/session';
import type {
  AnyPuzzleMode,
  BoardFeedback,
  DifficultyLevel,
  InputKey,
  PlayMode,
  PuzzleStatus,
  ResultSummary,
  ScoreBreakdown,
} from '../engine/types';
import { sounds } from '../hooks/useSound';
import { shortId } from '../utils/id';
import { clearSlice, loadSlice, saveSlice, type SliceConfig } from './persistence';
import { useSettings } from './SettingsContext';
import { liveStreak } from './statsLogic';
import { useStats } from './StatsContext';

export interface ResultData {
  breakdown: ScoreBreakdown;
  summary: ResultSummary;
}

/** A friend's result carried in a challenge link. */
export interface Challenge {
  beat?: number;
  by?: string;
}

export interface ActiveSession {
  /** Saved-slot key: one per daily (mode + day) and one endless per mode. */
  key: string;
  mode: AnyPuzzleMode;
  state: unknown;
  seed: SeedInfo;
  difficulty: DifficultyLevel;
  /** What the player asked for; `difficulty` may be eased below it. */
  requestedDifficultyId: string;
  playMode: PlayMode;
  /** A past day's daily, played from the archive (never touches streaks). */
  archive: boolean;
  /** The endless salt, so the exact puzzle can be shared as a link. */
  salt?: string;
  status: PuzzleStatus;
  startedAt: number;
  finishedAt: number | null;
  hintsUsed: number;
  hintPenalty: number;
  /** The last hint's message — an identical repeat is re-shown for free. */
  lastHint: string | null;
  moves: number;
  feedback: BoardFeedback | null;
  eased: boolean;
  forfeited: boolean;
  challenge: Challenge | null;
  result: ResultData | null;
}

export interface StartOptions {
  modeId: string;
  playMode: PlayMode;
  difficultyId?: string;
  /** Daily only: a past day to play from the archive (defaults to today). */
  day?: number;
  /** Endless only: reproduce a specific puzzle (from a challenge link). */
  salt?: string;
  /** Endless only: discard the parked endless puzzle and deal a new one. */
  fresh?: boolean;
  challenge?: Challenge | null;
}

/** 'done' = that daily is already finished (show its result instead). */
export type StartOutcome = 'started' | 'resumed' | 'done' | 'unavailable';

interface SerializedSession {
  modeId: string;
  seed: SeedInfo;
  difficultyId: string;
  requestedDifficultyId?: string;
  playMode: PlayMode;
  archive?: boolean;
  salt?: string;
  state: unknown;
  startedAt: number;
  hintsUsed: number;
  hintPenalty: number;
  lastHint?: string | null;
  moves?: number;
  eased: boolean;
  challenge?: Challenge | null;
  /** Last time this slot was touched — the most recent one is offered as "Continue". */
  touchedAt?: number;
}

type Slots = Record<string, SerializedSession>;

/** Pre-1.1 single-session slice, migrated into the slot map on first load. */
const legacySessionSlice: SliceConfig<SerializedSession | null> = {
  name: 'session',
  version: 1,
  defaults: () => null,
};

const slotsSlice: SliceConfig<Slots> = {
  name: 'sessions',
  version: 1,
  defaults: () => ({}),
};

export interface ParkedSlot {
  key: string;
  modeId: string;
  playMode: PlayMode;
  day?: number;
  touchedAt: number;
}

interface GameContextValue {
  active: ActiveSession | null;
  start: (opts: StartOptions) => StartOutcome;
  /** Resume a parked puzzle by its slot key. */
  resume: (key: string) => boolean;
  dispatchMove: (move: unknown) => void;
  handleKey: (key: InputKey) => void;
  requestHint: () => void;
  /** Step away from the board. An unfinished puzzle stays parked for later. */
  leave: () => void;
  /** Give up the puzzle on screen: it's recorded as a loss and the answer shown. */
  forfeit: () => void;
  /** Restart the clock if nothing has been played yet (e.g. after reading the rules). */
  restartClock: () => void;
  parked: ParkedSlot[];
  hasHint: boolean;
  celebrationNonce: number;
}

const GameContext = createContext<GameContextValue | null>(null);

export function slotKey(modeId: string, seed: SeedInfo): string {
  return seed.playMode === 'daily' ? `${modeId}:d${seed.dayNumber ?? 0}` : `${modeId}:e`;
}

function vibrate(pattern: number | number[], enabled: boolean): void {
  if (enabled && typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    try {
      navigator.vibrate(pattern);
    } catch {
      /* unsupported */
    }
  }
}

function serialize(s: ActiveSession): SerializedSession {
  return {
    modeId: s.mode.id,
    seed: s.seed,
    difficultyId: s.difficulty.id,
    requestedDifficultyId: s.requestedDifficultyId,
    playMode: s.playMode,
    archive: s.archive || undefined,
    salt: s.salt,
    state: s.mode.serialize(s.state),
    startedAt: s.startedAt,
    hintsUsed: s.hintsUsed,
    hintPenalty: s.hintPenalty,
    lastHint: s.lastHint,
    moves: s.moves,
    eased: s.eased,
    challenge: s.challenge,
    touchedAt: Date.now(),
  };
}

function hydrate(key: string, saved: SerializedSession): ActiveSession | null {
  const mode = getMode(saved.modeId);
  if (!mode || !saved.seed || typeof saved.seed.seed !== 'string') return null;
  try {
    const state = mode.deserialize(saved.state);
    if (state == null || mode.status(state) !== 'playing') return null;
    // A daily started on its own day still counts for that day if finished after midnight.
    const archive = saved.playMode === 'daily' && Boolean(saved.archive);
    return {
      key,
      mode,
      state,
      seed: saved.seed,
      difficulty: resolveDifficulty(mode, saved.difficultyId),
      requestedDifficultyId: saved.requestedDifficultyId ?? saved.difficultyId,
      playMode: saved.playMode === 'daily' ? 'daily' : 'endless',
      archive,
      salt: saved.salt,
      status: 'playing',
      startedAt: typeof saved.startedAt === 'number' ? saved.startedAt : Date.now(),
      finishedAt: null,
      hintsUsed: saved.hintsUsed || 0,
      hintPenalty: saved.hintPenalty || 0,
      lastHint: saved.lastHint ?? null,
      moves: saved.moves ?? 1,
      feedback: null,
      eased: Boolean(saved.eased),
      forfeited: false,
      challenge: saved.challenge ?? null,
      result: null,
    };
  } catch {
    return null;
  }
}

/** Load the slot map (migrating the legacy single session) and drop stale dailies. */
function loadSlots(today: number): Slots {
  const slots: Slots = { ...loadSlice(slotsSlice) };
  const legacy = loadSlice(legacySessionSlice);
  if (legacy && legacy.modeId && legacy.seed) {
    const key = slotKey(legacy.modeId, legacy.seed);
    if (!slots[key]) slots[key] = legacy;
    clearSlice('session');
  }
  for (const [key, s] of Object.entries(slots)) {
    const stale = s.playMode === 'daily' && (s.seed?.dayNumber ?? 0) < today - 1 && !s.archive;
    if (stale || !getMode(s.modeId)) delete slots[key];
  }
  return slots;
}

function parkedFrom(slots: Slots): ParkedSlot[] {
  return Object.entries(slots)
    .map(([key, s]) => ({
      key,
      modeId: s.modeId,
      playMode: s.playMode,
      day: s.playMode === 'daily' ? s.seed.dayNumber : undefined,
      touchedAt: s.touchedAt ?? s.startedAt ?? 0,
    }))
    .sort((a, b) => b.touchedAt - a.touchedAt);
}

export function GameProvider({ children }: { children: ReactNode }) {
  const settingsApi = useSettings();
  const statsApi = useStats();

  const slotsRef = useRef<Slots | null>(null);
  if (slotsRef.current === null) slotsRef.current = loadSlots(dayNumber());

  const [parked, setParked] = useState<ParkedSlot[]>(() => parkedFrom(slotsRef.current!));
  const [active, setActive] = useState<ActiveSession | null>(() => {
    // Reopen the most recently touched puzzle so "Continue" survives a reload.
    const newest = parkedFrom(slotsRef.current!)[0];
    return newest ? hydrate(newest.key, slotsRef.current![newest.key]) : null;
  });
  const [celebrationNonce, setCelebrationNonce] = useState(0);

  // Keep a synchronous mirror + latest deps so stable callbacks read fresh data.
  const activeRef = useRef(active);
  const depsRef = useRef({ settingsApi, statsApi });
  useEffect(() => {
    depsRef.current = { settingsApi, statsApi };
  });

  const writeSlots = useCallback((mutate: (slots: Slots) => void) => {
    const next = { ...slotsRef.current! };
    mutate(next);
    slotsRef.current = next;
    saveSlice(slotsSlice, next);
    const list = parkedFrom(next);
    // Only re-render consumers when the set/order of parked puzzles actually changed.
    setParked((prev) =>
      prev.length === list.length && prev.every((p, i) => p.key === list[i].key) ? prev : list,
    );
  }, []);

  const commit = useCallback((next: ActiveSession | null) => {
    activeRef.current = next;
    setActive(next);
  }, []);

  // Persist the puzzle on screen while it's in progress; free its slot once it ends.
  useEffect(() => {
    if (!active) return;
    try {
      if (active.status === 'playing') {
        const data = serialize(active);
        writeSlots((slots) => {
          slots[active.key] = data;
        });
      } else if (slotsRef.current![active.key]) {
        writeSlots((slots) => {
          delete slots[active.key];
        });
      }
    } catch {
      /* ignore serialization/storage issues */
    }
  }, [active, writeSlots]);

  const nextFeedback = (
    prev: BoardFeedback | null,
    message: string,
    tone: BoardFeedback['tone'],
  ): BoardFeedback => ({ message, tone, nonce: (prev?.nonce ?? 0) + 1 });

  const finalize = useCallback((session: ActiveSession, forfeited = false): ActiveSession => {
    const { statsApi: stats, settingsApi: settings } = depsRef.current;
    const finishedAt = Date.now();
    const elapsedMs = finishedAt - session.startedAt;
    const streak = session.archive ? 0 : liveStreak(stats.modeStats(session.mode.id), dayNumber());

    let breakdown: ScoreBreakdown;
    let summary: ResultSummary = session.mode.summarize(session.state);
    if (forfeited) {
      breakdown = { lines: [{ label: 'Gave up', value: 0, kind: 'base' }], total: 0 };
      summary = { ...summary, status: 'lost', headline: 'You gave up — here’s the answer' };
    } else {
      breakdown = finalizeScore({
        mode: session.mode,
        state: session.state,
        elapsedMs,
        difficulty: session.difficulty,
        streak,
        hintsUsed: session.hintsUsed,
        hintPenalty: session.hintPenalty,
      });
    }
    const status = summary.status === 'won' ? 'won' : 'lost';
    const isDaily = session.playMode === 'daily';

    stats.record({
      modeId: session.mode.id,
      playMode: session.playMode,
      status,
      score: breakdown.total,
      attempts: summary.attempts,
      answer: summary.answer,
      avoidKeys: summary.avoidKeys,
      difficultyId: session.difficulty.id,
      dayNumber: session.seed.dayNumber,
      archive: session.archive || undefined,
      hintsUsed: session.hintsUsed,
      seedLabel: session.seed.label,
      shareGrid: summary.shareGrid,
      completedAt: finishedAt,
      result: isDaily
        ? { breakdown, summary, difficultyId: session.difficulty.id, seedLabel: session.seed.label }
        : undefined,
    });

    if (status === 'won') {
      sounds.play('win');
      if (!settings.effectiveReducedMotion) setCelebrationNonce((n) => n + 1);
      vibrate([8, 40, 12], settings.settings.haptics);
    } else {
      sounds.play('lose');
      vibrate(24, settings.settings.haptics);
    }

    return { ...session, status, finishedAt, forfeited, result: { breakdown, summary } };
  }, []);

  const resume = useCallback(
    (key: string): boolean => {
      const current = activeRef.current;
      if (current?.key === key && current.status === 'playing') return true;
      const saved = slotsRef.current![key];
      const session = saved ? hydrate(key, saved) : null;
      if (!session) {
        if (saved) writeSlots((slots) => void delete slots[key]);
        return false;
      }
      sounds.resume();
      commit(session);
      return true;
    },
    [commit, writeSlots],
  );

  const start = useCallback(
    (opts: StartOptions): StartOutcome => {
      const { statsApi: stats } = depsRef.current;
      const mode = requireMode(opts.modeId);
      const today = dayNumber();

      let seed: SeedInfo;
      let difficulty: DifficultyLevel;
      let requestedDifficultyId: string;
      let eased = false;
      let archive = false;
      let salt: string | undefined;

      if (opts.playMode === 'daily') {
        const day = opts.day ?? today;
        if (!Number.isInteger(day) || day < 1 || day > today) return 'unavailable';
        seed = dailySeed(mode.id, dateForDay(day));
        archive = day < today;
        // Daily is shared: fixed difficulty, no easing, and it counts once.
        if (stats.daily(day, mode.id)) return 'done';
        difficulty = resolveDifficulty(mode, mode.defaultDifficultyId);
        requestedDifficultyId = difficulty.id;
      } else {
        requestedDifficultyId = resolveDifficulty(mode, opts.difficultyId ?? mode.defaultDifficultyId).id;
        if (opts.salt) {
          // A shared puzzle must match the sender's exactly: no easing.
          difficulty = resolveDifficulty(mode, requestedDifficultyId);
        } else {
          const adapted = adaptDifficulty(mode, requestedDifficultyId, stats.modeStats(mode.id).recentLossStreak);
          difficulty = adapted.difficulty;
          eased = adapted.eased;
        }
        salt = opts.salt ?? shortId(7);
        seed = endlessSeed(mode.id, salt);
      }

      const key = slotKey(mode.id, seed);
      const saved = slotsRef.current![key];
      const sameSeed = saved?.seed?.seed === seed.seed;
      const resumeParked = opts.playMode === 'daily' ? sameSeed : opts.salt ? sameSeed : !opts.fresh && !!saved;
      if (resumeParked && resume(key)) return 'resumed';

      sounds.resume();
      // Shared puzzles (dailies, challenge links) ignore avoid-recent so they stay identical.
      const avoid = opts.playMode === 'endless' && !opts.salt ? stats.recentAnswers(mode.id) : new Set<string>();
      const state = createPuzzle({ mode, seed, difficulty, avoid });

      commit({
        key,
        mode,
        state,
        seed,
        difficulty,
        requestedDifficultyId,
        playMode: opts.playMode,
        archive,
        salt,
        status: mode.status(state),
        startedAt: Date.now(),
        finishedAt: null,
        hintsUsed: 0,
        hintPenalty: 0,
        lastHint: null,
        moves: 0,
        feedback: null,
        eased,
        forfeited: false,
        challenge: opts.challenge ?? null,
        result: null,
      });
      return 'started';
    },
    [commit, resume],
  );

  const dispatchMove = useCallback(
    (move: unknown) => {
      const prev = activeRef.current;
      if (!prev || prev.status !== 'playing') return;
      const { settingsApi: settings } = depsRef.current;

      const validation = prev.mode.validateMove(prev.state, move);
      if (!validation.ok) {
        sounds.play('invalid');
        vibrate(18, settings.settings.haptics);
        commit({
          ...prev,
          feedback: nextFeedback(prev.feedback, validation.reason ?? 'Not allowed', 'error'),
        });
        return;
      }

      const state = prev.mode.reduce(prev.state, move);
      if (state === prev.state) return;
      const status = prev.mode.status(state);
      let said = null;
      try {
        said = prev.mode.moveFeedback?.(prev.state, state, move) ?? null;
      } catch {
        said = null;
      }
      if (said?.tone === 'error') {
        sounds.play('invalid');
        vibrate(18, settings.settings.haptics);
      }
      const stepped: ActiveSession = {
        ...prev,
        state,
        status,
        moves: prev.moves + 1,
        feedback: said ? nextFeedback(prev.feedback, said.message, said.tone) : null,
      };
      commit(status === 'playing' ? stepped : finalize(stepped));
    },
    [commit, finalize],
  );

  const handleKey = useCallback(
    (key: InputKey) => {
      const prev = activeRef.current;
      if (!prev || prev.status !== 'playing') return;
      const move = prev.mode.handleKey(prev.state, key);
      if (move != null) dispatchMove(move);
    },
    [dispatchMove],
  );

  const requestHint = useCallback(() => {
    const prev = activeRef.current;
    if (!prev || prev.status !== 'playing') return;

    const outcome = prev.mode.hint(prev.state);
    if (!outcome) {
      commit({ ...prev, feedback: nextFeedback(prev.feedback, 'No hint available.', 'info') });
      return;
    }

    // Same advice as last time and nothing revealed on the board: show it again, free.
    if (!outcome.state && outcome.message === prev.lastHint) {
      commit({ ...prev, feedback: nextFeedback(prev.feedback, outcome.message, 'info') });
      return;
    }

    sounds.play('hint');
    const state = outcome.state ?? prev.state;
    const status = prev.mode.status(state);
    const stepped: ActiveSession = {
      ...prev,
      state,
      status,
      hintsUsed: prev.hintsUsed + 1,
      hintPenalty: prev.hintPenalty + outcome.penalty,
      lastHint: outcome.message,
      feedback: nextFeedback(prev.feedback, outcome.message, 'info'),
    };
    commit(status === 'playing' ? stepped : finalize(stepped));
  }, [commit, finalize]);

  const leave = useCallback(() => {
    commit(null);
  }, [commit]);

  const forfeit = useCallback(() => {
    const prev = activeRef.current;
    if (!prev || prev.status !== 'playing') return;
    commit(finalize({ ...prev, feedback: null }, true));
  }, [commit, finalize]);

  const restartClock = useCallback(() => {
    const prev = activeRef.current;
    if (!prev || prev.status !== 'playing' || prev.moves > 0 || prev.hintsUsed > 0) return;
    commit({ ...prev, startedAt: Date.now() });
  }, [commit]);

  const hasHint = useMemo(() => {
    if (!active || active.status !== 'playing') return false;
    try {
      return active.mode.hint(active.state) != null;
    } catch {
      return false;
    }
  }, [active]);

  const value = useMemo<GameContextValue>(
    () => ({
      active,
      start,
      resume,
      dispatchMove,
      handleKey,
      requestHint,
      leave,
      forfeit,
      restartClock,
      parked,
      hasHint,
      celebrationNonce,
    }),
    [active, start, resume, dispatchMove, handleKey, requestHint, leave, forfeit, restartClock, parked, hasHint, celebrationNonce],
  );

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export function useGame(): GameContextValue {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error('useGame must be used within <GameProvider>');
  return ctx;
}
